import React, { useState, useEffect, useCallback } from 'react';
import { FaCrown, FaCheck, FaArrowUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../../styles/AdminViewPlans.css';

const AdminViewPlans = () => {
  const navigate = useNavigate();
  const [userCompany, setUserCompany] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const currencyRates = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    INR: 83.12,
    JPY: 149.50,
    AUD: 1.52,
    CAD: 1.36
  };

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$'
  };

  const apiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const token = sessionStorage.getItem('token');
  const currentPlanId = userCompany?.planId?._id || userCompany?.planId || '';
  const hasActivePlan = Boolean(currentPlanId || userCompany?.plan);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch user's company
      const companyRes = await fetch(`${apiUrl}/api/companies/user/company`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (companyRes.ok) {
        const companyData = await companyRes.json();
        setUserCompany(companyData.company);
      }

      // Fetch all plans
      const plansRes = await fetch(`${apiUrl}/api/plans`);
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setAllPlans(plansData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load plans data');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpgrade = async (newPlan) => {
    if (!userCompany) {
      setError('Please add company details first');
      return;
    }

    if (newPlan._id === userCompany.planId?._id) {
      setError('You are already on this plan');
      return;
    }

    try {
      setError('');
      setSuccess('');

      // Update company with new plan details
      const updateRes = await fetch(`${apiUrl}/api/companies/${userCompany._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planId: newPlan._id,
          plan: newPlan.name,
          price: newPlan.price,
          users: newPlan.users,
          assets: newPlan.assets
        })
      });

      if (updateRes.ok) {
        const updatedCompany = await updateRes.json();
        const company = updatedCompany.company || updatedCompany;
        
        // Update local state with new company data
        setUserCompany(company);
        
        // Show success message with updated limits
        setSuccess(`✓ Successfully upgraded to ${newPlan.name} plan! New limits: ${newPlan.users} users, ${newPlan.assets} assets`);
        
        // Refetch data to ensure consistency across all components
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        const errorData = await updateRes.json();
        setError(errorData.message || 'Failed to upgrade plan');
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
      setError('Failed to upgrade plan. Please try again.');
    }
  };

  const handleBuyNow = (plan) => {
    if (!plan?._id) return;
    navigate(`/admin/buy-plan?planId=${plan._id}`);
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading plans...</div>;
  }

  return (
    <div className="admin-plans-container">
      <div className="plans-header">
        <h2> View & Upgrade Plans</h2>
        <p>Manage your subscription and view available plans</p>
      </div>

      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      {success && (
        <div className="message success">
          {success}
        </div>
      )}

      {/* Current Plan Section */}
      {userCompany && hasActivePlan ? (
        <div className="current-plan-section">
          <h3>Your Current Plan</h3>
          <div className="current-plan-card">
            <div className="current-plan-top">
              <div className="current-plan-main">
                <p className="current-plan-caption">Plan</p>
                <h4>{userCompany.planId?.name || userCompany.plan}</h4>
                <p className="current-plan-price">
                  {currencySymbols[selectedCurrency]}{((Number(userCompany.price) || 0) * currencyRates[selectedCurrency]).toFixed(0)}
                  <span>/month</span>
                </p>
              </div>

              <div className="current-plan-badges">
                <span className={`status-badge-pill ${String(userCompany.status || '').toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                  {String(userCompany.status || 'unknown').toUpperCase()}
                </span>
                <span className={`days-pill ${Number(userCompany.daysRemaining || 0) < 30 ? 'warning' : 'good'}`}>
                  {Number(userCompany.daysRemaining || 0)} days left
                </span>
              </div>
            </div>

            <div className="current-plan-metrics">
              <div className="metric-chip">
                <label>Users Limit</label>
                <p>{Number(userCompany.users || 0)}</p>
              </div>

              <div className="metric-chip">
                <label>Assets Limit</label>
                <p>{Number(userCompany.assets || 0)}</p>
              </div>

              <div className="metric-chip">
                <label>Company</label>
                <p>{userCompany.name || userCompany.companyName || '-'}</p>
              </div>

              <div className="metric-chip">
                <label>Billing</label>
                <p>Monthly</p>
              </div>
            </div>
          </div>
        </div>
      ) : userCompany ? (
        <div style={{ padding: '20px', background: '#fff3cd', border: '1px solid #ffca2c', borderRadius: '6px', color: '#664d03', marginBottom: '20px' }}>
          <strong>No active plan found.</strong> Please choose a plan and click Buy Now to continue.
        </div>
      ) : (
        <div style={{ padding: '20px', background: '#fef3cd', border: '1px solid #ffc107', borderRadius: '6px', color: '#856404', marginBottom: '20px' }}>
          ℹ️ Please add company details in the Profile section to view your plan and upgrade options.
        </div>
      )}

      {/* Currency Selector */}
      <div className="currency-selector">
        <label>Display Currency:</label>
        <select 
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value)}
        >
          {Object.keys(currencySymbols).map(curr => (
            <option key={curr} value={curr}>{curr} ({currencySymbols[curr]})</option>
          ))}
        </select>
      </div>

      {/* Available Plans */}
      <div className="available-plans-section">
        <h3>🎯 Available Plans</h3>
        <div className="plans-grid">
          {allPlans.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666' }}>No plans available</p>
          ) : (
            allPlans.map(plan => {
              const isCurrentPlan = Boolean(
                userCompany && hasActivePlan && (
                  (currentPlanId && String(plan._id) === String(currentPlanId)) ||
                  (!currentPlanId && userCompany.plan === plan.name)
                )
              );
              return (
                <div key={plan._id} className={`plan-card ${plan.highlighted ? 'highlighted' : ''} ${isCurrentPlan ? 'current' : ''}`}>
                  {plan.highlighted && (
                    <div className="popular-badge">
                      <FaCrown /> RECOMMENDED
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="current-badge">
                      ✓ YOUR PLAN
                    </div>
                  )}

                  <div className="plan-header">
                    <h4>{plan.name}</h4>
                    <p className="description">{plan.description}</p>
                  </div>

                  <div className="plan-pricing">
                    <div className="price-wrapper">
                      {plan.originalPrice && (
                        <span className="original-price">
                          {currencySymbols[selectedCurrency]}{(plan.originalPrice * currencyRates[selectedCurrency]).toFixed(0)}
                        </span>
                      )}
                      <span className="currency">{currencySymbols[selectedCurrency]}</span>
                      <span className="price">{(plan.price * currencyRates[selectedCurrency]).toFixed(0)}</span>
                    </div>
                    <span className="period">/month</span>
                  </div>

                  <div className="plan-limits">
                    <div className="limit-badge">
                      <strong>Assets:</strong>
                      <span>{plan.assets === 999 ? 'Unlimited' : plan.assets}</span>
                    </div>
                    <div className="limit-badge">
                      <strong>Users:</strong>
                      <span>{plan.users === 999 ? 'Unlimited' : plan.users}</span>
                    </div>
                  </div>

                  <div className="plan-features">
                    <p className="features-title">Includes:</p>
                    <ul>
                      {plan.features.map((feature, idx) => (
                        <li key={`${plan._id}-feature-${idx}`}>
                          <FaCheck className="check-icon" /> {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="plan-actions">
                    {!isCurrentPlan && userCompany && hasActivePlan ? (
                      <button 
                        onClick={() => handleUpgrade(plan)}
                        className="plan-btn primary"
                      >
                        <FaArrowUp /> Upgrade to {plan.name}
                      </button>
                    ) : !isCurrentPlan && userCompany && !hasActivePlan ? (
                      <button
                        onClick={() => handleBuyNow(plan)}
                        className="plan-btn primary"
                      >
                        Buy Now
                      </button>
                    ) : isCurrentPlan ? (
                      <button 
                        disabled
                        className="plan-btn success"
                        style={{ opacity: 0.8, cursor: 'default' }}
                      >
                        ✓ Current Plan
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminViewPlans;
